import React, { useState, useEffect } from 'react';
import { Upload, FileText, CheckCircle, XCircle, Clock, Download, Server, Cpu, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_URL } from '../config';

const ClusterLicensesPage = () => {
  const [clusterLicenses, setClusterLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [isUploadExpanded, setIsUploadExpanded] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Set page title
  useEffect(() => {
    document.title = 'Cluster Licenses | Customer Portal';
  }, []);

  useEffect(() => {
    fetchMyLicenses();
  }, []);

  const fetchMyLicenses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/cluster-licenses/my-licenses`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        // Backend returns { data: [...] }, extract the array
        const data = result.data || result;
        const licenses = Array.isArray(data) ? data : [];
        setClusterLicenses(licenses);
        // Auto-collapse upload section if user has licenses
        if (licenses.length > 0) {
          setIsUploadExpanded(false);
        }
      } else {
        console.error('[CLUSTER LICENSES] Response not OK:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Failed to fetch cluster licenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    const filename = file.name.toLowerCase();
    
    // Check file extension
    if (!filename.endsWith('.xlic')) {
      toast.error('Please select a .xlic file');
      return;
    }
    
    // Check filename pattern: cluster-request-{hostname}.xlic
    if (!filename.match(/^cluster-request-[a-z0-9-]+\.xlic$/i)) {
      toast.error('Invalid filename format. Expected: cluster-request-{hostname}.xlic');
      return;
    }

    if (file.size > 50 * 1024) {
      toast.error('File size must be less than 50KB');
      return;
    }

    setSelectedFile(file);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const uploadClusterLicense = async () => {
    if (!selectedFile) {
      toast.error('Please select a cluster license file (.xlic)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('notes', notes);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/cluster-licenses/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Cluster license request uploaded successfully! Our team will configure and sign it shortly.');
        setSelectedFile(null);
        setNotes('');
        fetchMyLicenses();
      } else {
        toast.error(data.error || 'Failed to upload cluster license request');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload cluster license request');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadLicense = async (licenseId, hostname) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/cluster-licenses/download/${licenseId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download license');
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cluster-license-${hostname || licenseId}.xlic`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('License downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download license');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: {
        icon: Clock,
        className: 'bg-yellow-100 text-yellow-800',
        label: 'Pending Configuration',
      },
      APPROVED: {
        icon: CheckCircle,
        className: 'bg-green-100 text-green-800',
        label: 'License Ready',
      },
      REJECTED: {
        icon: XCircle,
        className: 'bg-red-100 text-red-800',
        label: 'Rejected',
      },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Cluster Licenses</h1>
        <p className="mt-1 text-sm text-gray-500">
          Upload cluster license requests and download signed licenses
        </p>
      </div>

      {/* Upload Section - Collapsible */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <button
          onClick={() => setIsUploadExpanded(!isUploadExpanded)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <Plus className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-left">
              <h2 className="text-lg font-semibold text-gray-900">
                {clusterLicenses.length === 0 ? 'Upload Your First Cluster License Request' : 'Upload New Cluster License Request'}
              </h2>
              <p className="text-sm text-gray-500">
                {isUploadExpanded ? 'Click to collapse' : 'Click to expand upload form'}
              </p>
            </div>
          </div>
          {isUploadExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {isUploadExpanded && (
          <div className="px-6 pb-6 pt-2 border-t border-gray-100">
            <p className="text-sm text-gray-600 mb-4">
              Upload your <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">.xlic</code> cluster license request file. Maximum file size is 50KB.
            </p>

            <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive
              ? 'border-green-500 bg-green-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          
          {selectedFile ? (
            <div className="space-y-2">
              <div className="flex items-center justify-center space-x-2">
                <FileText className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-gray-900">{selectedFile.name}</span>
              </div>
              <p className="text-xs text-gray-500">
                {(selectedFile.size / 1024).toFixed(2)} KB
              </p>
              <button
                onClick={() => setSelectedFile(null)}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Remove file
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-2">
                Drag and drop your cluster license request file (.xlic) here
              </p>
              <p className="text-xs text-gray-500 mb-4">or</p>
              <label className="cursor-pointer">
                <span className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 inline-block">
                  Browse Files
                </span>
                <input
                  type="file"
                  accept=".xlic"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
              </label>
              <p className="text-xs text-gray-500 mt-4">
                Maximum file size: 50KB | Supported format: .xlic
              </p>
            </>
          )}
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any additional information or special requirements..."
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

            <button
              onClick={uploadClusterLicense}
              disabled={!selectedFile || uploading}
              className="mt-4 w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Cluster License Request
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Information Box - Compact */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
        <p className="text-xs text-blue-800">
          💡 <strong>Quick Tip:</strong> Upload .xlic cluster files • Get email alerts when configured • Download signed license for deployment
        </p>
      </div>

      {/* My Cluster Licenses */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold">My Cluster License Requests</h2>
          <p className="text-xs text-gray-600 mt-1">Total: {clusterLicenses.length} requests</p>
        </div>
        
        {loading ? (
          <div className="p-6 text-center text-gray-500 text-sm">
            Loading...
          </div>
        ) : clusterLicenses.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-sm">
            <Server className="mx-auto h-10 w-10 text-gray-400 mb-2" />
            <p>No cluster license requests yet</p>
            <p className="text-xs mt-1">Upload your first cluster license request above</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                      Hostname
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                      Cluster Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                      License Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                      Submitted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(() => {
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const paginatedLicenses = clusterLicenses.slice(startIndex, endIndex);
                    
                    return paginatedLicenses.map((license) => (
                  <tr key={license.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {license.hostname || 'Unknown'}
                      </div>
                      <div className="text-xs text-gray-500">{license.filename}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3 text-xs">
                        <div className="flex items-center">
                          <Server className="w-3 h-3 mr-1 text-gray-400" />
                          <span>{license.totalHosts} host{license.totalHosts !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex items-center">
                          <Cpu className="w-3 h-3 mr-1 text-gray-400" />
                          <span>{license.totalCores} cores</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {license.licenseType ? (
                        <div className="text-xs">
                          <div className="font-medium">{license.licenseType} / {license.licenseTier}</div>
                          {license.expiresAt && (
                            <div className="text-gray-500">
                              Expires: {new Date(license.expiresAt).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Awaiting configuration</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(license.status)}
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-500">
                      {formatDate(license.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      {license.status === 'APPROVED' && license.signedFilePath ? (
                        <button
                          onClick={() => handleDownloadLicense(license.id, license.hostname)}
                          className="inline-flex items-center px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download License
                        </button>
                      ) : license.status === 'REJECTED' && license.responseNotes ? (
                        <div className="text-xs text-red-600">
                          <div className="font-medium">Rejected</div>
                          <div className="text-gray-600">{license.responseNotes}</div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Processing...</span>
                      )}
                    </td>
                  </tr>
                ));
                  })()}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            {clusterLicenses.length > itemsPerPage && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {Math.min((currentPage - 1) * itemsPerPage + 1, clusterLicenses.length)} to {Math.min(currentPage * itemsPerPage, clusterLicenses.length)} of {clusterLicenses.length} requests
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {(() => {
                    const totalPages = Math.ceil(clusterLicenses.length / itemsPerPage);
                    const pages = [];
                    for (let i = 1; i <= totalPages; i++) {
                      if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => setCurrentPage(i)}
                            className={`px-3 py-1 border rounded text-sm ${
                              currentPage === i
                                ? 'bg-green-600 text-white border-green-600'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      } else if (i === currentPage - 2 || i === currentPage + 2) {
                        pages.push(<span key={i} className="px-2">...</span>);
                      }
                    }
                    return pages;
                  })()}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(Math.ceil(clusterLicenses.length / itemsPerPage), prev + 1))}
                    disabled={currentPage === Math.ceil(clusterLicenses.length / itemsPerPage)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClusterLicensesPage;
